import Axios from 'axios'
import { getUid, getToken } from '@/utils'

import snackbar from '@/utils/snackbar'
import { useUserStore, useAppStore } from '@/store/app'
import pinia from '@/plugins'
import { addPending, removePending } from './plugin'

const store = useUserStore(pinia)
const appStore = useAppStore(pinia)

const service = Axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 600000,
  showToast: true, // 默认需要错误提示
})

/**
 * @description HTTP 错误码转错误提示文本
 * @param {AxiosResponse} response Axios response object
 */
const handleNetworkError = (response) => {
  /** http status code */
  const code = response.status
  /** notice text */
  let message = '请求失败，请稍后重试'
  if (code) {
    switch (code) {
      case 400:
        message = '参数不正确！'
        break
      case 401:
        message = '登录超时，请重新登录后再进行操作！'
        break
      case 403:
        message = '您没有权限操作'
        break
      case 404:
        message = '访问资源不存在'
        break
      case 408:
        message = '请求超时'
        break
      case 500:
        message = '服务器内部错误！'
        break
      case 501:
        message = '承载服务未实现！'
        break
      case 502:
        message = '网关错误！'
        break
      case 503:
        message = '服务暂不可用！'
        break
      case 504:
        message = '网关超时！'
        break
      case 505:
        message = 'HTTP版本不受支持！'
        break
      default:
        message = '异常问题，请联系管理员！'
    }
  } else {
    message = `无法连接到服务器！`
  }

  if (response.config?.showToast) {
    snackbar.error(message)
  }
}

/**
 * @description 请求发起前的拦截器
 * @returns {AxiosRequestConfig} config
 */
service.interceptors.request.use(async (config) => {
  config.headers.token = getToken()
  config.headers.requestId = `${getUid()}`

  // 在请求开始前，对之前的请求做检查取消操作
  await removePending(config)
  // 将当前请求添加到 pending 中
  await addPending(config)
  return config
})

/**
 * @description 响应收到后的拦截器
 * @returns {Promise}
 */
service.interceptors.response.use(
  /** 2xx 范围内的状态码都会触发该函数。 */
  async (response) => {
    // 在请求结束后，移除本次请求信息
    removePending(response.config)

    if (response.status === 200) {
      // Prioritize checking for blob response type to ensure headers are preserved for file downloads.
      if (response.config.responseType === 'blob') {
        return Promise.resolve(response)
      }

      if (
        response.data.code === '0' ||
        response.data.code === 0 ||
        response.data.statusCode === 200
      ) {
        // Handle regular successful API responses
        if (response.data.total) {
          return Promise.resolve(response.data)
        }
        return Promise.resolve(response.data.data)
      }

      console.error('error resoponse: ', response.data)
      let __text = response?.data?.message || '请求失败，请稍后重试'

      if (response.data.code === 403) {
        __text = `Sorry，您暂无${response.data?.path}接口权限，请联系管理员`
      }
      if (response.data.code === 401 || response.data.code === '401') {
        snackbar.error('认证信息过期，请重新登录')
        if (import.meta.env.MODE !== 'development') {
          setTimeout(() => {
            appStore.clearAppInfo()
            store.clearUserInfo()
            window.location.replace(import.meta.env.VITE_APP_LOGIN_PAGE)
          }, 1500)
        }

        return Promise.reject(new Error('401'))
      }
      if (response.config.showToast) {
        snackbar.error(__text)
      }
      return Promise.reject(new Error(__text))
    }
    if (response.config.showToast) {
      snackbar.error('请求失败，请稍后重试')
    }
    return Promise.reject(new Error(response.data.message))
  },
  /** 超出 2xx 范围的状态码都会触发该函数。 */
  async (error) => {
    if (Axios.isCancel(error)) {
      return Promise.reject(new Error('cancel request'))
    }
    if (error?.response?.status === 401) {
      // if (router.currentRoute.value.path !== "/login") {
      // message.info("登录凭证已过期，请重新登录");
      // router.push("/login");
      // }
      return Promise.reject(new Error('401'))
    }
    handleNetworkError(error?.response)

    return Promise.reject(error?.response)
  }
)

export default service
